import { DocumentItemModel } from '../../../models'
import { normalizeNumberString, numberAsText } from '../../../utils/formatters'
import round from '../../../utils/round'

export interface QuickItemDraft {
    name: string
    quantity: string
    price: string
    discount: string
    initialQuantity: string
    initialDiscount: string
}

export type QuickItemDraftErrors = Partial<Record<'name' | 'quantity' | 'price' | 'discount', string>>

type TranslationFunction = (key: string, options?: {}) => string

interface QuickItemOptions {
    index: number
    defaultVat: number
    isAccountingVat: boolean
    enterFullPriceEnabled: boolean
}

export const getQuickItemValidationMessages = (t: TranslationFunction, enterFullPriceEnabled = false) => ({
    required: (fieldName: string) => t('Allaround.Validation.required', { fieldName }),
    number: (fieldName: string) => t('Allaround.Validation.number', { fieldName }),
    nonZeroQuantity: t('Allaround.Validation.incorrectEntry', { fieldName: t('Invoice.Edit.quantity') }),
    negativeDiscount: t('Allaround.Validation.discountNegative'),
    excessiveDiscount: t('Allaround.Validation.discountMoreThan100'),
    fieldNames: {
        name: t('Invoice.Table.Header.name'),
        quantity: t('Invoice.Edit.quantity'),
        // The price field holds the line total when full price entry is on, so the validation
        // messages have to name the column the user is actually typing into.
        price: enterFullPriceEnabled ? t('Invoice.Table.Header.total') : t('Invoice.Table.Header.price'),
        discount: t('Invoice.Edit.discount'),
    },
})

export const createQuickItemDraft = (defaultDiscount: number): QuickItemDraft => {
    const discount = numberAsText(round(defaultDiscount))

    return {
        name: '',
        quantity: '1',
        price: '',
        discount,
        initialQuantity: '1',
        initialDiscount: discount,
    }
}

export const parseQuickItemNumber = (value: string): number | null => {
    const trimmedValue = value.trim()

    if (!/^-?[0-9.,]+$/.test(trimmedValue)) return null

    const normalized = normalizeNumberString(trimmedValue)

    if (!normalized) return null

    const parsed = Number.parseFloat(normalized)

    return Number.isFinite(parsed) ? parsed : null
}

const parseQuickItemDiscount = (value: string): number | null => {
    return value.trim() === '' ? 0 : parseQuickItemNumber(value)
}

const numericValuesMatch = (left: string, right: string) => {
    return parseQuickItemNumber(left) === parseQuickItemNumber(right)
}

export const isQuickItemDraftEmpty = (draft: QuickItemDraft) => {
    return (
        draft.name.trim() === '' &&
        draft.price.trim() === '' &&
        numericValuesMatch(draft.quantity, draft.initialQuantity) &&
        parseQuickItemDiscount(draft.discount) === parseQuickItemDiscount(draft.initialDiscount)
    )
}

export const validateQuickItemDraft = (
    draft: QuickItemDraft,
    messages: ReturnType<typeof getQuickItemValidationMessages>
): QuickItemDraftErrors => {
    if (isQuickItemDraftEmpty(draft)) return {}

    const errors: QuickItemDraftErrors = {}
    const quantity = parseQuickItemNumber(draft.quantity)
    const price = parseQuickItemNumber(draft.price)
    const discount = parseQuickItemDiscount(draft.discount)

    if (!draft.name.trim()) errors.name = messages.required(messages.fieldNames.name)

    if (!draft.quantity.trim()) errors.quantity = messages.required(messages.fieldNames.quantity)
    else if (quantity == null) errors.quantity = messages.number(messages.fieldNames.quantity)
    else if (quantity === 0) errors.quantity = messages.nonZeroQuantity

    if (!draft.price.trim()) errors.price = messages.required(messages.fieldNames.price)
    else if (price == null) errors.price = messages.number(messages.fieldNames.price)

    if (discount == null) errors.discount = messages.number(messages.fieldNames.discount)
    else if (discount < 0) errors.discount = messages.negativeDiscount
    else if (discount >= 100) errors.discount = messages.excessiveDiscount

    return errors
}

export const quickItemDraftToDocumentItem = (draft: QuickItemDraft, options: QuickItemOptions): DocumentItemModel => {
    const quantity = parseQuickItemNumber(draft.quantity)!
    const price = parseQuickItemNumber(draft.price)!
    const discount = parseQuickItemDiscount(draft.discount)!
    const vat = options.isAccountingVat ? options.defaultVat : 0
    const discountMultiplier = 1 - discount / 100

    let salePrice: number
    let fullPrice: number

    if (options.enterFullPriceEnabled) {
        // The typed price is the line total, exactly like the editable total cell on saved rows,
        // so the unit price is derived back out of it (see `changeItemField`).
        fullPrice = round(price)
        salePrice = round(round(round(fullPrice / quantity) / discountMultiplier) / (1 + vat / 100))
    } else {
        salePrice = price
        fullPrice = round(round(round(round(salePrice * quantity) * (1 + vat / 100))) * discountMultiplier)
    }

    return {
        id: 0,
        index: options.index,
        name: draft.name.trim(),
        quantity,
        salePrice,
        fullPrice,
        discount,
        vat,
        unit: {
            existsInSettings: false,
            name: '',
        },
        dimension: '',
        additionalNote: '',
        purchasePrice: 0,
        taxFree: false,
    }
}

export const quickItemDraftTotal = (draft: QuickItemDraft, options: QuickItemOptions) => {
    const item = quickItemDraftToDocumentItem(draft, options)

    if (options.enterFullPriceEnabled) return item.fullPrice

    return round(item.salePrice * item.quantity * (1 - item.discount / 100))
}

// Unit price shown next to a line total the user typed, matching the read-only price cell of
// saved rows.
export const quickItemDraftUnitPrice = (draft: QuickItemDraft, options: QuickItemOptions) => {
    const item = quickItemDraftToDocumentItem(draft, options)

    if (!item.fullPrice) return 0

    return round(round(item.fullPrice / item.quantity / (1 - item.discount / 100)))
}
